import React, { useState, useEffect } from 'react';
import { Accessibility, ZoomIn, ZoomOut, Eye, EyeOff, MousePointer, Volume2, VolumeX, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';

interface AccessibilitySettings {
  fontSize: number;
  highContrast: boolean;
  focusOutlines: boolean;
  reducedMotion: boolean;
  screenReader: boolean;
  largerCursor: boolean;
}

const AccessibilityToolbar = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>({
    fontSize: 100,
    highContrast: false,
    focusOutlines: false,
    reducedMotion: false,
    screenReader: false,
    largerCursor: false,
  });

  const texts = {
    en: {
      accessibility: 'Accessibility',
      textSize: 'Text Size',
      increase: 'Increase',
      decrease: 'Decrease',
      contrast: 'High Contrast',
      focus: 'Focus Outlines',
      motion: 'Reduce Motion',
      screenReader: 'Screen Reader',
      cursor: 'Large Cursor',
      reset: 'Reset All',
      close: 'Close'
    },
    he: {
      accessibility: 'נגישות',
      textSize: 'גודל טקסט',
      increase: 'הגדל',
      decrease: 'הקטן',
      contrast: 'ניגודיות גבוהה',
      focus: 'מיקוד קווי מתאר',
      motion: 'הפחתת תנועה',
      screenReader: 'קורא מסך',
      cursor: 'סמן גדול',
      reset: 'איפוס הכל',
      close: 'סגור'
    }
  };

  const t = texts[language as keyof typeof texts] || texts.en;

  useEffect(() => {
    const savedSettings = localStorage.getItem('accessibilitySettings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  const applySettings = (newSettings: AccessibilitySettings) => {
    const root = document.documentElement;
    
    // Font size
    root.style.fontSize = `${newSettings.fontSize}%`;
    
    // High contrast
    if (newSettings.highContrast) {
      root.classList.add('accessibility-high-contrast');
    } else {
      root.classList.remove('accessibility-high-contrast');
    }
    
    // Focus outlines
    if (newSettings.focusOutlines) {
      root.classList.add('accessibility-focus-outlines');
    } else {
      root.classList.remove('accessibility-focus-outlines');
    }
    
    // Reduced motion
    if (newSettings.reducedMotion) {
      root.classList.add('accessibility-reduced-motion');
    } else {
      root.classList.remove('accessibility-reduced-motion');
    }
    
    // Large cursor
    if (newSettings.largerCursor) {
      root.classList.add('accessibility-large-cursor');
    } else {
      root.classList.remove('accessibility-large-cursor');
    }
    
    // Screen reader announcements
    if (newSettings.screenReader) {
      announceToScreenReader('Screen reader mode activated');
    }
  };

  const announceToScreenReader = (message: string) => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      setSettings(prev => ({ ...prev, fontSize: prev.fontSize + 10 }));
    }
  };

  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      setSettings(prev => ({ ...prev, fontSize: prev.fontSize - 10 }));
    }
  };

  const toggleSetting = (key: keyof AccessibilitySettings) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resetAll = () => {
    const defaultSettings = {
      fontSize: 100,
      highContrast: false,
      focusOutlines: false,
      reducedMotion: false,
      screenReader: false,
      largerCursor: false,
    };
    setSettings(defaultSettings);
    announceToScreenReader('All accessibility settings reset');
  };

  return (
    <>
      {/* Accessibility Styles */}
      <style>{`
        .accessibility-high-contrast {
          filter: contrast(150%) brightness(120%);
        }
        
        .accessibility-focus-outlines *:focus {
          outline: 3px solid hsl(var(--primary)) !important;
          outline-offset: 2px !important;
        }
        
        .accessibility-reduced-motion * {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
        
        .accessibility-large-cursor * {
          cursor: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="M2 2l28 28M2 30l28-28" stroke="black" stroke-width="2"/></svg>') 16 16, auto !important;
        }
        
        .sr-only {
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
        }
      `}</style>

      {/* Floating Accessibility Button */}
      <div className="fixed bottom-4 left-4 z-50">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className="rounded-full w-12 h-12 p-0 shadow-lg bg-medical-dark hover:bg-medical-dark/90"
          aria-label={t.accessibility}
          aria-expanded={isOpen}
        >
          <Accessibility className="h-6 w-6" />
        </Button>

        {/* Accessibility Panel */}
        {isOpen && (
          <Card className="absolute bottom-16 left-0 w-80 p-4 shadow-xl bg-background border border-border">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">
                  {t.accessibility}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  aria-label={t.close}
                >
                  ✕
                </Button>
              </div>

              {/* Font Size Controls */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  {t.textSize}: {settings.fontSize}%
                </label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={decreaseFontSize}
                    disabled={settings.fontSize <= 80}
                    aria-label={t.decrease}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={increaseFontSize}
                    disabled={settings.fontSize >= 150}
                    aria-label={t.increase}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Toggle Options */}
              <div className="space-y-2">
                <Button
                  variant={settings.highContrast ? "default" : "outline"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => toggleSetting('highContrast')}
                  aria-pressed={settings.highContrast}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  {t.contrast}
                </Button>

                <Button
                  variant={settings.focusOutlines ? "default" : "outline"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => toggleSetting('focusOutlines')}
                  aria-pressed={settings.focusOutlines}
                >
                  <MousePointer className="h-4 w-4 mr-2" />
                  {t.focus}
                </Button>

                <Button
                  variant={settings.reducedMotion ? "default" : "outline"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => toggleSetting('reducedMotion')}
                  aria-pressed={settings.reducedMotion}
                >
                  <EyeOff className="h-4 w-4 mr-2" />
                  {t.motion}
                </Button>

                <Button
                  variant={settings.screenReader ? "default" : "outline"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => toggleSetting('screenReader')}
                  aria-pressed={settings.screenReader}
                >
                  <Volume2 className="h-4 w-4 mr-2" />
                  {t.screenReader}
                </Button>

                <Button
                  variant={settings.largerCursor ? "default" : "outline"}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => toggleSetting('largerCursor')}
                  aria-pressed={settings.largerCursor}
                >
                  <MousePointer className="h-4 w-4 mr-2" />
                  {t.cursor}
                </Button>
              </div>

              {/* Reset Button */}
              <Button
                variant="destructive"
                size="sm"
                className="w-full"
                onClick={resetAll}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                {t.reset}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </>
  );
};

export default AccessibilityToolbar;